import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";

// Import pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import ChangePassword from "./pages/ChangePassword";
import ResetPassword from "./pages/ResetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import LandingPage from "./pages/LandingPage";

// Import admin components
import Inventory from "./components/admin/Inventory";
import Users from "./components/admin/Users";
import CategoryManagement from "./components/admin/CategoryManagement";
import LabManagement from "./components/admin/LabManagement";
import BookingSystem from "./components/admin/BookingSystem";
import SystemLogbook from "./components/admin/SystemLogbook";
import AdminMaintenancePanel from "./components/admin/AdminMaintenancePanel";
import WorkerManagement from "./components/admin/WorkerManagement";

// Import developer components - Developer Master tier
import DeveloperDashboard from "./components/developer/DeveloperDashboard";
import DepartmentManagement from "./components/developer/DepartmentManagement";
import AdminManagement from "./components/developer/AdminManagement";
import SystemSettings from "./components/developer/SystemSettings";
import DeveloperMasterPanel from "./components/developer/DeveloperMasterPanel";
import AutoReviewConfig from "./components/developer/AutoReviewConfig";
import DepartmentSettings from "./components/developer/DepartmentSettings";
// Faculty components
import FacultyDashboard from "./components/faculty/FacultyDashboard";
import UserManagement from "./components/faculty/UserManagement";
import TokenManagement from "./components/faculty/TokenManagement";

// Scholar components
import ScholarDashboard from "./components/scholar/ScholarDashboard";

// User components
import UserDashboard from "./components/user/UserDashboard";

// Worker components
import WorkerDashboard from "./components/worker/WorkerDashboard";
// Manager components
import ManagerDashboard from "./components/manager/ManagerDashboard";
import ManagerManagement from "./components/developer/ManagerManagement";

// HOD components
import HODDashboard from "./components/hod/HODDashboardNew";
import HODTeamManagement from "./components/hod/HODTeamManagement";
import HODCategoryRequests from "./components/hod/HODCategoryRequests";

// Protected Route wrapper component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();

  // Show loading state if auth is still being checked
  if (loading) {
    return <div>Loading...</div>;
  }

  // If not authenticated, redirect to login page
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // If authenticated, render the protected component
  return children;
};

// Auth Route for login/register (redirects if already logged in)
const AuthRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();

  // Show loading state if auth is still being checked
  if (loading) {
    return <div>Loading...</div>;
  }

  // If authenticated, redirect to dashboard
  if (currentUser) {
    return <Navigate to="/dashboard" replace />;
  }

  // If not authenticated, render the login/register component
  return children;
};

// Role-based route (only allows specified roles)
const RoleRoute = ({ children, allowedRoles }) => {
  const { currentUser, userProfile, loading } = useAuth();

  // Show loading state if auth is still being checked
  if (loading) {
    return <div>Loading...</div>;
  }

  // If not authenticated, redirect to login page
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Get role from userProfile or from currentUser
  const userRole = userProfile?.role || currentUser.role;

  console.log("RoleRoute checking access:", {
    allowedRoles,
    userRole,
    hasAccess: allowedRoles.includes(userRole),
  });

  // If authenticated but not in allowed roles, redirect to dashboard
  if (!userRole || !allowedRoles.includes(userRole)) {
    console.log("User doesn't have required role, redirecting to dashboard");
    return <Navigate to="/dashboard" replace />;
  }

  // If authenticated and has allowed role, render the protected component
  console.log("User has required role, rendering protected component");
  return children;
};

const AppRouter = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <Routes>
            {/* Public Routes */}
            {/* Landing page is now the root entry. Always render it so every new session begins here. */}
            <Route path="/" element={<LandingPage />} />
            <Route
              path="/login"
              element={
                <AuthRoute>
                  <Login />
                </AuthRoute>
              }
            />
            <Route
              path="/register"
              element={
                <AuthRoute>
                  <Register />
                </AuthRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <AuthRoute>
                  <ForgotPassword />
                </AuthRoute>
              }
            />

            {/* Protected Routes (require authentication) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Change Password Route */}
            {/* Change Password (first-time or manual) */}
            <Route
              path="/change-password"
              element={
                <AuthRoute>
                  <ChangePassword />
                </AuthRoute>
              }
            />
            {/* Password Reset via OTP/link */}
            <Route
              path="/reset-password"
              element={
                <AuthRoute>
                  <ResetPassword />
                </AuthRoute>
              }
            />

            {/* Developer Master Routes */}
            <Route
              path="/developer"
              element={
                <RoleRoute allowedRoles={["developer-master"]}>
                  <DeveloperDashboard />
                </RoleRoute>
              }
            />

            <Route
              path="/developer/departments"
              element={
                <RoleRoute allowedRoles={["developer-master"]}>
                  <DepartmentManagement />
                </RoleRoute>
              }
            />

            <Route
              path="/developer/admins"
              element={
                <RoleRoute allowedRoles={["developer-master"]}>
                  <AdminManagement />
                </RoleRoute>
              }
            />

            <Route
              path="/developer/settings"
              element={
                <RoleRoute allowedRoles={["developer-master"]}>
                  <SystemSettings />
                </RoleRoute>
              }
            />
            <Route
              path="/developer/auto-review"
              element={
                <RoleRoute allowedRoles={["developer-master"]}>
                  <AutoReviewConfig />
                </RoleRoute>
              }
            />
            <Route
              path="/developer/managers"
              element={
                <RoleRoute allowedRoles={["developer-master"]}>
                  <ManagerManagement />
                </RoleRoute>
              }
            />

            {/* Developer Master Panel */}
            <Route
              path="/developer-master"
              element={
                <RoleRoute allowedRoles={["developer-master"]}>
                  <DeveloperMasterPanel />
                </RoleRoute>
              }
            />

            <Route
              path="/developer-master/department-settings"
              element={
                <RoleRoute allowedRoles={["developer-master"]}>
                  <DepartmentSettings />
                </RoleRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/inventory"
              element={
                <RoleRoute allowedRoles={["admin", "faculty", "scholar"]}>
                  <Inventory />
                </RoleRoute>
              }
            />

            <Route
              path="/users"
              element={
                <RoleRoute allowedRoles={["admin"]}>
                  <Users />
                </RoleRoute>
              }
            />

            <Route
              path="/category-management"
              element={
                <RoleRoute allowedRoles={["admin"]}>
                  <CategoryManagement />
                </RoleRoute>
              }
            />

            <Route
              path="/lab-management"
              element={
                <RoleRoute allowedRoles={["admin"]}>
                  <LabManagement />
                </RoleRoute>
              }
            />

            <Route
              path="/booking-system"
              element={
                <RoleRoute allowedRoles={["admin", "faculty", "scholar"]}>
                  <BookingSystem />
                </RoleRoute>
              }
            />

            {/* Admin System Logbook */}
            <Route
              path="/admin/logbook"
              element={
                <RoleRoute allowedRoles={["admin", "developer-master"]}>
                  <SystemLogbook />
                </RoleRoute>
              }
            />

            {/* Admin Maintenance Panel */}
            <Route
              path="/admin/maintenance"
              element={
                <RoleRoute allowedRoles={["manager", "developer-master"]}>
                  <AdminMaintenancePanel />
                </RoleRoute>
              }
            />

            {/* Admin Worker Management */}
            <Route
              path="/admin/workers"
              element={
                <RoleRoute allowedRoles={["manager", "developer-master"]}>
                  <WorkerManagement />
                </RoleRoute>
              }
            />

            {/* Faculty Dashboard */}
            <Route
              path="/faculty"
              element={
                <RoleRoute allowedRoles={["faculty"]}>
                  <FacultyDashboard />
                </RoleRoute>
              }
            />
            {/* Faculty User Management */}
            <Route
              path="/faculty/users"
              element={
                <RoleRoute allowedRoles={["faculty"]}>
                  <UserManagement />
                </RoleRoute>
              }
            />
            {/* Faculty Registration Tokens */}
            <Route
              path="/faculty/tokens"
              element={
                <RoleRoute allowedRoles={["faculty"]}>
                  <TokenManagement />
                </RoleRoute>
              }
            />

            {/* Scholar Dashboard */}
            <Route
              path="/scholar"
              element={
                <RoleRoute allowedRoles={["scholar"]}>
                  <ScholarDashboard />
                </RoleRoute>
              }
            />

            {/* User Dashboard */}
            <Route
              path="/user"
              element={
                <RoleRoute allowedRoles={["user"]}>
                  <UserDashboard />
                </RoleRoute>
              }
            />

            {/* Worker Dashboard */}
            <Route
              path="/worker"
              element={
                <RoleRoute allowedRoles={["worker"]}>
                  <WorkerDashboard />
                </RoleRoute>
              }
            />

            {/* HOD Dashboard */}
            <Route
              path="/hod"
              element={
                <RoleRoute allowedRoles={["hod"]}>
                  <HODDashboard />
                </RoleRoute>
              }
            />

            {/* HOD Team Management */}
            <Route
              path="/hod/team"
              element={
                <RoleRoute allowedRoles={["hod"]}>
                  <HODTeamManagement />
                </RoleRoute>
              }
            />

            {/* HOD Category Requests */}
            <Route
              path="/hod/requests"
              element={
                <RoleRoute allowedRoles={["hod"]}>
                  <HODCategoryRequests />
                </RoleRoute>
              }
            />

            {/* Manager Dashboard (global maintenance manager) */}
            <Route
              path="/manager"
              element={
                <RoleRoute allowedRoles={["manager", "developer-master"]}>
                  <ManagerDashboard />
                </RoleRoute>
              }
            />

            {/* Fallback route for undefined paths */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default AppRouter;
